import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Phone, ArrowLeft, QrCode, Key, Code, Copy, RefreshCw,
  Smartphone, CheckCircle2, Trash2, ExternalLink, AlertTriangle
} from 'lucide-react';
import QRCode from 'react-qr-code';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = window.location.origin;
const PRODUCTION_URL = 'https://www.viralehooks.store';

export default function SettingsPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  // QR Pairing
  const [pairingToken, setPairingToken] = useState(null);
  const [pairingLoading, setPairingLoading] = useState(false);

  // API Key
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);

  // Data Reset
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const handleResetData = async () => {
    setResetLoading(true);
    try {
      const res = await axios.delete(`${API}/data/reset`, { headers });
      setResetResult(res.data);
      setShowResetConfirm(false);
      toast.success(`Gelöscht: ${res.data.deleted_leads} Leads, ${res.data.deleted_calls} Anrufe, ${res.data.deleted_sms} SMS`);
    } catch {
      toast.error('Fehler beim Zurücksetzen');
    }
    setResetLoading(false);
  };

  useEffect(() => {
    fetchApiKey();
  }, []);

  const fetchApiKey = async () => {
    try {
      const res = await axios.get(`${API}/settings/api-key`, { headers });
      setApiKey(res.data.api_key);
    } catch { /* ignore */ }
    setApiKeyLoading(false);
  };

  const generatePairingToken = async () => {
    setPairingLoading(true);
    try {
      const res = await axios.post(`${API}/pairing/generate`, {}, { headers });
      setPairingToken(res.data.pairing_token);
      toast.success('QR-Code generiert (5 Minuten gültig)');
    } catch {
      toast.error('Fehler beim Generieren');
    }
    setPairingLoading(false);
  };

  const generateApiKey = async () => {
    try {
      const res = await axios.post(`${API}/settings/api-key`, {}, { headers });
      setApiKey(res.data.api_key);
      setShowKey(true);
      toast.success('API-Schlüssel generiert');
    } catch {
      toast.error('Fehler beim Generieren');
    }
  };

  const revokeApiKey = async () => {
    try {
      await axios.delete(`${API}/settings/api-key`, { headers });
      setApiKey(null);
      setShowKey(false);
      toast.success('API-Schlüssel widerrufen');
    } catch {
      toast.error('Fehler beim Widerrufen');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In die Zwischenablage kopiert');
  };

  const qrUrl = pairingToken ? `${APP_URL}/pair?token=${pairingToken}` : '';

  const integrationSnippet = `<!-- CallRelay Integration -->
<script>
(function() {
  var CALLRELAY_URL = '${PRODUCTION_URL}/api';
  var API_KEY = '${apiKey || 'DEIN_API_KEY'}';

  window.callRelayTrigger = function(phoneNumber, leadName, leadId) {
    fetch(CALLRELAY_URL + '/external/trigger-call', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      },
      body: JSON.stringify({
        phone_number: phoneNumber,
        lead_name: leadName || '',
        lead_id: leadId || ''
      })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (data.delivered) {
        alert('Anruf an Handy gesendet: ' + phoneNumber);
      } else {
        alert('Kein Handy verbunden. Bitte CallRelay Mobile öffnen.');
      }
    })
    .catch(function() {
      alert('Fehler beim Senden des Anrufs.');
    });
  };
})();
</script>`;

  const emergentInstructions = `Bitte füge in meinem Recruiting-Programm (Factory-Hype / Berater Dashboard) folgende Änderungen hinzu:

1. TELEFON-FELD KLICKBAR MACHEN:
   Im "Bewerber-Details" Bereich soll das Telefon-Feld klickbar sein.
   Wenn der Berater auf die Telefonnummer klickt, soll folgende JavaScript-Funktion aufgerufen werden:

   callRelayTrigger(telefonnummer, bewerbername, bewerberid)

   Beispiel:
   callRelayTrigger('0171219911', 'BDV GmbH', 'lead-123')

2. SCRIPT EINBINDEN:
   Bitte füge folgendes Script in die Seite ein (z.B. im <head> oder vor </body>):

${integrationSnippet}

3. TELEFON-FELD STYLING:
   Das Telefon-Feld soll einen Hover-Effekt bekommen:
   - Cursor: pointer
   - Farbe bei Hover: grün (#059669)
   - Ein kleines Telefon-Icon daneben

4. OPTIONAL - "Anruf ans Handy" Button:
   Neben dem "Als kontaktiert markieren" Button kann optional ein neuer Button
   "Anruf ans Handy senden" hinzugefügt werden, der ebenfalls callRelayTrigger() aufruft.

WICHTIG:
- Die CallRelay API URL ist: ${PRODUCTION_URL}/api/external/trigger-call
- Der API-Schlüssel muss im Header "X-API-Key" mitgeschickt werden
- API-Schlüssel: ${apiKey || '(Bitte zuerst in CallRelay generieren)'}`;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="settings-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Dashboard
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Einstellungen
          </h1>
        </div>
        <span className="text-sm text-slate-500">{user?.name}</span>
      </header>

      {/* Content */}
      <main className="p-6 max-w-4xl mx-auto">
        <Tabs defaultValue="pairing" className="w-full">
          <TabsList className="rounded-sm bg-slate-100 mb-6" data-testid="settings-tabs">
            <TabsTrigger value="pairing" className="rounded-sm gap-2">
              <QrCode className="w-4 h-4" />
              Geräte-Kopplung
            </TabsTrigger>
            <TabsTrigger value="integration" className="rounded-sm gap-2">
              <Code className="w-4 h-4" />
              Integration
            </TabsTrigger>
            <TabsTrigger value="apikey" className="rounded-sm gap-2">
              <Key className="w-4 h-4" />
              API-Schlüssel
            </TabsTrigger>
            <TabsTrigger value="data" className="rounded-sm gap-2">
              <Trash2 className="w-4 h-4" />
              Daten
            </TabsTrigger>
          </TabsList>

          {/* ── QR Pairing Tab ── */}
          <TabsContent value="pairing">
            <Card className="rounded-sm shadow-sm">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Handy per QR-Code verbinden
                </CardTitle>
                <CardDescription>
                  Scanne den QR-Code mit deinem Handy, um die CallRelay App direkt zu öffnen und dich automatisch anzumelden.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!pairingToken ? (
                  <div className="text-center space-y-4 py-8">
                    <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                      <Smartphone className="w-10 h-10 text-slate-300" />
                    </div>
                    <Button
                      onClick={generatePairingToken}
                      disabled={pairingLoading}
                      className="rounded-sm bg-black text-white hover:bg-slate-800"
                      data-testid="generate-qr-button"
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      {pairingLoading ? 'Generieren...' : 'QR-Code generieren'}
                    </Button>
                    <p className="text-xs text-slate-400">Der QR-Code ist 5 Minuten gültig</p>
                  </div>
                ) : (
                  <div className="text-center space-y-4" data-testid="qr-code-display">
                    <div className="bg-white p-6 rounded-sm border border-slate-200 inline-block mx-auto">
                      <QRCode value={qrUrl} size={200} level="M" />
                    </div>
                    <p className="text-sm text-slate-500">
                      Scanne diesen QR-Code mit der Kamera deines Handys
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <Badge variant="outline" className="rounded-sm text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        5 Min. gültig
                      </Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={generatePairingToken}
                      className="rounded-sm"
                      data-testid="regenerate-qr-button"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Neuen QR-Code generieren
                    </Button>
                  </div>
                )}

                <Separator />

                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Alternative: Manuell anmelden</p>
                  <p className="text-sm text-slate-500">
                    Öffne auf deinem Handy im Browser:{' '}
                    <code className="bg-slate-100 px-2 py-0.5 rounded-sm text-xs font-mono">
                      {APP_URL}/mobile
                    </code>
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(`${APP_URL}/mobile`)}
                    className="rounded-sm"
                    data-testid="copy-mobile-url"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    URL kopieren
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Integration Tab ── */}
          <TabsContent value="integration">
            <div className="space-y-6">
              <Card className="rounded-sm shadow-sm">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Recruiting-Programm anbinden
                  </CardTitle>
                  <CardDescription>
                    Verbinde dein bestehendes Recruiting-Programm (z.B. Factory-Hype) mit CallRelay,
                    damit du direkt aus dem Bewerberprofil anrufen kannst.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Step 1 */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-black text-white rounded-sm flex items-center justify-center text-xs font-bold">1</div>
                      <h3 className="font-medium text-slate-900">API-Schlüssel generieren</h3>
                    </div>
                    <p className="text-sm text-slate-500 ml-8">
                      Gehe zum Tab "API-Schlüssel" und generiere einen Schlüssel. Diesen brauchst du für die Integration.
                    </p>
                    {apiKey && (
                      <div className="ml-8 flex items-center gap-2">
                        <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          API-Schlüssel vorhanden
                        </Badge>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Step 2 */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-black text-white rounded-sm flex items-center justify-center text-xs font-bold">2</div>
                      <h3 className="font-medium text-slate-900">Code-Snippet kopieren</h3>
                    </div>
                    <p className="text-sm text-slate-500 ml-8">
                      Dieses JavaScript-Snippet muss in dein Recruiting-Programm eingebaut werden:
                    </p>
                    <div className="ml-8 relative">
                      <pre className="bg-slate-900 text-slate-100 p-4 rounded-sm text-xs overflow-x-auto font-mono leading-relaxed max-h-64">
                        {integrationSnippet}
                      </pre>
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2 rounded-sm bg-slate-800 text-white border-slate-700 hover:bg-slate-700"
                        onClick={() => copyToClipboard(integrationSnippet)}
                        data-testid="copy-snippet-button"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Kopieren
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  {/* Step 3 */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-black text-white rounded-sm flex items-center justify-center text-xs font-bold">3</div>
                      <h3 className="font-medium text-slate-900">Telefonnummer klickbar machen</h3>
                    </div>
                    <p className="text-sm text-slate-500 ml-8">
                      Im Recruiting-Programm muss die Telefonnummer einen onClick-Handler bekommen:
                    </p>
                    <div className="ml-8">
                      <pre className="bg-slate-900 text-slate-100 p-4 rounded-sm text-xs overflow-x-auto font-mono">
{`// Beispiel: Telefonnummer klickbar machen
<span 
  onclick="callRelayTrigger('0171219911', 'BDV GmbH', 'lead-123')"
  style="cursor: pointer; color: #059669;"
>
  0171219911
</span>`}
                      </pre>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Emergent Instructions */}
              <Card className="rounded-sm shadow-sm border-2 border-emerald-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <ExternalLink className="w-5 h-5 text-emerald-600" />
                    Anleitung für Emergent
                  </CardTitle>
                  <CardDescription>
                    Kopiere diesen Text und gib ihn bei Emergent ein, um die Integration in deinem Recruiting-Programm zu erstellen.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <pre className="bg-slate-50 border border-slate-200 p-4 rounded-sm text-sm text-slate-700 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
                      {emergentInstructions}
                    </pre>
                    <Button
                      onClick={() => copyToClipboard(emergentInstructions)}
                      className="absolute top-2 right-2 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid="copy-emergent-instructions"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Anleitung kopieren
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* API Documentation */}
              <Card className="rounded-sm shadow-sm">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                    API-Dokumentation
                  </CardTitle>
                  <CardDescription>
                    Basis-URL: <code className="bg-slate-100 px-2 py-0.5 rounded-sm text-xs font-mono">{PRODUCTION_URL}</code>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Call Endpoint */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold text-slate-800">Anruf auslösen</h4>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Endpoint:</p>
                      <code className="block bg-slate-100 p-3 rounded-sm text-sm font-mono">
                        POST {PRODUCTION_URL}/api/external/trigger-call
                      </code>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Headers:</p>
                      <code className="block bg-slate-100 p-3 rounded-sm text-sm font-mono">
                        Content-Type: application/json<br />
                        X-Api-Key: {apiKey ? '***' + apiKey.slice(-8) : '<DEIN_API_KEY>'}
                      </code>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Body:</p>
                      <pre className="bg-slate-100 p-3 rounded-sm text-sm font-mono">
{JSON.stringify({
  phone_number: "+4917612345678",
  lead_name: "Max Mustermann",
  lead_id: "optional-crm-id"
}, null, 2)}
                      </pre>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Antwort:</p>
                      <pre className="bg-slate-100 p-3 rounded-sm text-sm font-mono">
{JSON.stringify({
  id: "call-uuid",
  status: "pending",
  delivered: true,
  devices_connected: 1
}, null, 2)}
                      </pre>
                    </div>
                  </div>

                  <Separator />

                  {/* SMS Endpoint */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold text-slate-800">SMS auslösen</h4>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Endpoint:</p>
                      <code className="block bg-slate-100 p-3 rounded-sm text-sm font-mono">
                        POST {PRODUCTION_URL}/api/external/trigger-sms
                      </code>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Body:</p>
                      <pre className="bg-slate-100 p-3 rounded-sm text-sm font-mono">
{JSON.stringify({
  phone_number: "+4917612345678",
  message: "Hallo, wir laden Sie ein...",
  lead_name: "Max Mustermann",
  lead_id: "optional-crm-id"
}, null, 2)}
                      </pre>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── API Key Tab ── */}
          <TabsContent value="apikey">
            <Card className="rounded-sm shadow-sm">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                  API-Schlüssel
                </CardTitle>
                <CardDescription>
                  Mit dem API-Schlüssel kann dein Recruiting-Programm Anrufe über CallRelay auslösen.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {apiKeyLoading ? (
                  <p className="text-slate-400 text-sm">Laden...</p>
                ) : apiKey ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Input
                          readOnly
                          value={showKey ? apiKey : '••••••••••••' + apiKey.slice(-8)}
                          className="rounded-sm font-mono text-sm"
                          data-testid="api-key-display"
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowKey(!showKey)}
                        className="rounded-sm shrink-0"
                        data-testid="toggle-key-visibility"
                      >
                        {showKey ? 'Verbergen' : 'Anzeigen'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(apiKey)}
                        className="rounded-sm shrink-0"
                        data-testid="copy-api-key"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        onClick={generateApiKey}
                        className="rounded-sm"
                        data-testid="regenerate-api-key"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Neuen Schlüssel generieren
                      </Button>
                      <Button
                        variant="outline"
                        onClick={revokeApiKey}
                        className="rounded-sm text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                        data-testid="revoke-api-key"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Widerrufen
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400">
                      Halte den API-Schlüssel geheim. Teile ihn nur mit vertrauenswürdigen Systemen.
                    </p>
                  </div>
                ) : (
                  <div className="text-center space-y-4 py-8">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                      <Key className="w-8 h-8 text-slate-300" />
                    </div>
                    <p className="text-sm text-slate-500">Noch kein API-Schlüssel generiert</p>
                    <Button
                      onClick={generateApiKey}
                      className="rounded-sm bg-black text-white hover:bg-slate-800"
                      data-testid="generate-api-key"
                    >
                      <Key className="w-4 h-4 mr-2" />
                      API-Schlüssel generieren
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Data Reset Tab ── */}
          <TabsContent value="data">
            <Card className="rounded-sm shadow-sm border-red-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Daten zurücksetzen
                </CardTitle>
                <CardDescription>
                  Lösche alle gespeicherten Leads, Anrufe und SMS auf einmal. Diese Aktion kann nicht rückgängig gemacht werden.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {resetResult && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 space-y-1" data-testid="reset-result">
                    <p className="text-sm font-medium text-emerald-800">Erfolgreich gelöscht:</p>
                    <p className="text-sm text-emerald-700">{resetResult.deleted_leads} Leads, {resetResult.deleted_calls} Anrufe, {resetResult.deleted_sms} SMS</p>
                  </div>
                )}

                {!showResetConfirm ? (
                  <Button
                    onClick={() => setShowResetConfirm(true)}
                    variant="outline"
                    className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    data-testid="reset-data-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Alle Daten löschen
                  </Button>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-sm p-4 space-y-4" data-testid="reset-confirm-dialog">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-red-800">Sind Sie sicher?</p>
                        <p className="text-sm text-red-600 mt-1">
                          Alle Leads, Anrufe und SMS werden unwiderruflich gelöscht. Der API-Schlüssel bleibt erhalten.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={handleResetData}
                        disabled={resetLoading}
                        className="rounded-sm bg-red-600 text-white hover:bg-red-700"
                        data-testid="confirm-reset-button"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {resetLoading ? 'Lösche...' : 'Ja, alles löschen'}
                      </Button>
                      <Button
                        onClick={() => setShowResetConfirm(false)}
                        variant="outline"
                        className="rounded-sm"
                        data-testid="cancel-reset-button"
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
